import { getJsonLdImages } from '../getJsonLdImages/getJsonLdImages';
import { getProductOffer } from '../getProductOffer/getProductOffer';
import { HENI_ORGANIZATION_REF } from '#lib/features/seo/constants/heni-organization.js';
import { OfferAvailability } from '#lib/features/seo/constants/offer-availability.js';
import type { ShopPdpBook } from '#lib/websites/shop/pages/ShopBookPdp/types.js';
import { omitEmpty } from '$global/utils/omitEmpty/omitEmpty';
import { stripMarkdown } from '$global/utils/stripMarkdown/stripMarkdown';

// `format` is free text; map only what BookFormatType can express.
const getBookFormat = (format: string | null | undefined) => {
	if (/hard/i.test(format ?? '')) return 'https://schema.org/Hardcover';
	if (/paper|soft/i.test(format ?? '')) return 'https://schema.org/Paperback';
	return undefined;
};

type PriceRange = (number | null)[];

// Mirrors the series page: one price when the options agree, else "From £min",
// always in GBP and regardless of stock.
const getSeriesOffer = ([low, high]: PriceRange) => {
	const single = low === high || !low || !high ? low || high : null;
	const lowPrice = single ?? low;
	return lowPrice
		? omitEmpty({
				'@type': 'AggregateOffer',
				lowPrice,
				highPrice: single ?? undefined,
				priceCurrency: 'GBP',
				seller: HENI_ORGANIZATION_REF,
			})
		: undefined;
};

// `priceRange` is set on the series page, which shows a price per series.
export const getBookJsonLd = (
	book: ShopPdpBook,
	url: string,
	priceRange?: PriceRange | null
) => {
	// The PDP prints a price (sale price first) only when in stock.
	const bookOffer = book.product?.in_stock
		? getProductOffer({
				price: book.product.sale_price || book.product.price,
				currency: book.product.currency,
				availability: book.product.can_pre_order
					? OfferAvailability.PreOrder
					: OfferAvailability.InStock,
				url,
			})
		: undefined;
	const offers = priceRange ? getSeriesOffer(priceRange) : bookOffer;

	// Google flags a Product without offers as invalid.
	return omitEmpty({
		'@context': 'https://schema.org',
		'@type': offers ? ['Product', 'Book'] : 'Book',
		'@id': `${url}#product`,
		url,
		name: book.title,
		description: stripMarkdown(book.description ?? '').trim(),
		image: getJsonLdImages([
			book.featured_image,
			...(book.gallery ?? []).map((junction) => junction?.directus_files_id),
		]),
		isbn: book.isbn,
		numberOfPages: Number.parseInt(book.number_of_pages ?? '', 10) || undefined,
		datePublished: book.publication_date,
		bookFormat: getBookFormat(book.format),
		author: (book.authors ?? [])
			.map((junction) => junction?.Publishing_Authors_And_Artists_id?.full_name)
			.filter(Boolean)
			.map((name) => ({ '@type': 'Person', name })),
		brand: offers ? HENI_ORGANIZATION_REF : undefined,
		offers,
	});
};
